import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAddressStore } from '@features/shipping-methods/model/useAddressStore'
import { AddressItemType } from '@features/shipping-methods/types'

import { useMyAddressesQuery } from '@shared/generated'
import Button from '@shared/ui/button'

import styles from './styles.module.scss'



const DEFAULT_CENTER: [number, number] = [ 50.300377, 57.154555 ]
const COORDINATE_THRESHOLD = 0.0001

interface MapDeliveryProps {
  newAddress: boolean;
  onChangeAddressMap: ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => void;
}

export default function MapDelivery (props: MapDeliveryProps) {
  const { setSelectedAddress, setDeliveryType } = useAddressStore()
  const { data: addressData } = useMyAddressesQuery()
  const { t: tc } = useTranslation('common')
  const { newAddress, onChangeAddressMap } = props
  const mapRef = useRef<ymaps.Map | null>(null)
  const [ isMapReady, setMapReady ] = useState(false)
  const [ mapCenter ] = useState<number[]>(DEFAULT_CENTER)
  const [ initialCoords, setInitialCoords ] = useState<number[] | null>(null)
  const [ currentCoords, setCurrentCoords ] = useState<number[] | null>(null)

  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       setMapCenter([latitude, longitude]);
  //     },
  //     () => {
  //       setMapCenter(DEFAULT_CENTER);
  //     },
  //   );
  // }, []);

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return

    const map = mapRef.current

    const handleMapMove = async () => {
      const center = map.getCenter()
      setCurrentCoords(center)
    }

    map.events.add('actionend', handleMapMove)

    return () => {
      map.events.remove('actionend', handleMapMove)
    }
  }, [ isMapReady ])

  const handleAddressSelect = () => {
    if (currentCoords) {
      onChangeAddressMap({
        latitude: currentCoords[0],
        longitude: currentCoords[1],
      })
      setInitialCoords(currentCoords)
    }
  }

  const hasCoordinatesChanged = () => {
    if (!initialCoords || !currentCoords) return false

    return (
      Math.abs(initialCoords[0] - currentCoords[0]) > COORDINATE_THRESHOLD ||
      Math.abs(initialCoords[1] - currentCoords[1]) > COORDINATE_THRESHOLD
    )
  }

  return (
    <div className={styles.map}>
      <YMaps
        query={{
          apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
          load: 'package.full',
        }}
      >
        <Map
          defaultState={{
            center: mapCenter,
            zoom: 14,
            controls: [ 'zoomControl' ],
          }}
          width="100%"
          height="100%"
          instanceRef={(ref) => {
            if (ref) {
              mapRef.current = ref
              setMapReady(true)

              if (!initialCoords) {
                setInitialCoords(mapCenter)
                setCurrentCoords(mapCenter)
              }
            }
          }}
          options={{
            suppressMapOpenBlock: true,
            copyrightLogoVisible: false,
            copyrightUaVisible: false,
          }}
          
        >
          {!newAddress &&
            addressData?.myAddresses?.map((item) => {
              return (
                <Placemark
                  key={item?.id}
                  geometry={[
                    item?.customerGeoPoint?.latitude,
                    item?.customerGeoPoint?.longitude,
                  ]}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref: '/icons/map-pin-green.svg',
                    iconImageSize: [ 36, 55 ],
                    iconImageOffset: [ -16, -32 ],
                  }}
                  onClick={() => {
                    setSelectedAddress(item as AddressItemType)
                    setDeliveryType('delivery')
                  }}
                />
              )
            })}
        </Map>
      </YMaps>

      {newAddress && (
        <div className={styles.map__center}>
          <div className={styles['map__center-marker']}></div>
          {hasCoordinatesChanged() && (
            <Button
              type="button"
              className={styles['map__center-select']}
              onClick={handleAddressSelect}
            >
              {tc('address.choose')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
